import Styles from './styles.module.css';
import '../../salon/auth/login/tokens.css';
import React, { useContext, useEffect, useRef, useState } from 'react';
import NoIndex from '../../components/NoIndex';
import {
  IoInformationCircleOutline,
  IoCreateOutline,
  IoHeartOutline,
  IoSwapVerticalOutline,
  IoHelpCircleOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoExpandOutline,
  IoBulbOutline,
  IoArrowUpOutline,
  IoCompassOutline,
  IoAlertCircleOutline,
} from 'react-icons/io5';

/*
 * YICビューティモード専門学校向け「ガイダンス」回答ページ(/guidance-yic、src/pages/guidance)の
 * 使い方マニュアル。school/student/salon と同じ構成・ルールで作成(docs/ai/manual-pattern.md 参照)。
 *
 * /guidance-yic はログイン不要の公開ページで、校内ガイダンス当日に話を聞きたいサロンを学生が
 * 事前に回答するための仕組み。学校ごとに回答方式が異なり(src/pages/guidance/config.ts)、
 * YICビューティモード専門学校は「順位付け方式」(第1志望〜第6志望。rankCount=6)。
 * このマニュアルは、志望群方式(第1志望群/第2志望群)の学校(群馬県美容専門学校など)には
 * 対応していない(手順・画面が異なるため)。
 *
 * 画面のスクリーンショットは public/guidance-yic-manual/ に置き、/guidance-yic-manual/xxx.jpg で
 * 参照する。すべてスマートフォン表示(375x812)で、本番の /guidance-yic を実際に操作して撮影した。
 * ただし「確定」「内定承諾済み」を押すとその場で本番のFirestore(guidance コレクション)に
 * 実データが書き込まれ、学校側の「ガイダンス回答状況」タブにも反映されてしまうため、その2つの
 * ボタンは実際には押していない(押す前の確認画面・エラー表示までを撮影している)。
 */

type Section = {
  id: string;
  title: string;
  icon: React.ReactNode;
};

// 画像を拡大表示するためのライトボックスを開く関数。Shot は本文中の色々な場所から
// (Props を毎回渡さず) 呼べるよう、Context 経由で GuidanceYicManual 側の setZoom を受け取る
const ZoomContext = React.createContext<(src: string, alt: string) => void>(() => {});

// 画像のどこをタップ・操作すればいいかを示す番号ピン。x/y は画像に対する位置の割合(0-100)で
// 指定するので、表示サイズが変わっても同じ場所を指せる
type Hotspot = { x: number; y: number; n: number };

// 画面のスクリーンショット1枚。キャプション付きで、枠線のついたカードとして表示する。
// 画像をクリック(タップ)すると、元の解像度に近いサイズで大きく表示する。hotspots を渡すと、
// 本文の手順(①②③)と対応する番号ピンを画像上に重ねて表示する
//
// ── caption を書くかどうかのルール(StepList との重複を防ぐため。manual/school・manual/student と共通) ──
// 1. この Shot の直前(同じ SubHeading 内)に StepList があり、かつ hotspots を付けている場合、
//    caption に手順をもう一度書かない。「①…②…」のように StepList の文言をなぞる書き出しは
//    禁止。caption は省略するか、StepList に書いていない新情報だけを書く。
// 2. StepList が無い単独の Shot(操作の起点ではなく結果画面・状態の対比だけを見せるもの)には、
//    画面が何かを示す caption を必ずつける。
// 3. 判断に迷ったら「StepList を声に出して読んだときに、caption と同じ内容を2回言っていないか」
//    を基準にする。
const Shot: React.FC<{ src: string; alt: string; caption?: string; hotspots?: Hotspot[] }> = ({ src, alt, caption, hotspots }) => {
  const openZoom = useContext(ZoomContext);
  return (
    <figure className={Styles.shot}>
      <button type="button" className={Styles.shotButton} onClick={() => openZoom(src, alt)} aria-label={`${alt}を拡大表示`}>
        <img src={src} alt={alt} loading="lazy" />
        {hotspots?.map((h, i) => (
          <span key={`${i}-${h.x}-${h.y}`} className={Styles.hotspot} style={{ left: `${h.x}%`, top: `${h.y}%` }}>{h.n}</span>
        ))}
        <span className={Styles.shotZoomHint}><IoExpandOutline /></span>
      </button>
      {caption && <figcaption className={Styles.shotCaption}>{caption}</figcaption>}
    </figure>
  );
};

// 画面の説明だけでなく、実際にどう使うかを示す「活用のヒント」の箱
const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.tipBox}>
    <IoBulbOutline className={Styles.tipBoxIcon} />
    <div className={Styles.tipBoxBody}>
      <p className={Styles.tipBoxTitle}>活用のヒント</p>
      <ul className={Styles.tipBoxList}>{children}</ul>
    </div>
  </div>
);

// 操作手順を①②③の番号付きで見せるための一覧
const StepList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ol className={Styles.stepList}>{children}</ol>
);
const Step: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={Styles.step}>{children}</li>
);

// 操作を間違えやすいポイントの注意書き(黄色)
const Caution: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={Styles.caution}>
    <IoAlertCircleOutline className={Styles.cautionIcon} />
    <div>{children}</div>
  </div>
);

// 各セクションの冒頭で「この画面で何ができるか」を1行でまとめる太字のリード文
const SectionLead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={Styles.sectionLead}>{children}</p>
);

// セクション内を機能のまとまりごとに分ける小見出し
const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={Styles.subHeading}>{children}</h3>
);

const SECTIONS: Section[] = [
  { id: 'start', title: 'はじめに', icon: <IoInformationCircleOutline /> },
  { id: 'info', title: '学生情報を入力する', icon: <IoCreateOutline /> },
  { id: 'search', title: '気になるサロンを選ぶ', icon: <IoHeartOutline /> },
  { id: 'order', title: '志望の順位をつける', icon: <IoSwapVerticalOutline /> },
  { id: 'faq', title: 'よくある質問', icon: <IoHelpCircleOutline /> },
];

// 「やりたいことから探す」の導線。機能名を覚えていなくても、目的の文章から該当の章へ
// 直接ジャンプできるようにする(ページ冒頭に並べる)
const GOALS: { label: string; id: string }[] = [
  { label: 'まず全体の流れを知りたい', id: 'start' },
  { label: '学生情報の入力でつまずいた', id: 'info' },
  { label: '気になるサロンの選び方・絞り込み方を知りたい', id: 'search' },
  { label: '志望の順位のつけ方を知りたい', id: 'order' },
  { label: '「内定承諾済み」を押してしまった・困っている', id: 'faq' },
];

const GuidanceYicManual: React.FC = () => {
  const [ activeId, setActiveId ] = useState<string>(SECTIONS[0].id);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // クリックで拡大表示中のスクリーンショット(無ければ非表示)
  const [ zoom, setZoom ] = useState<{ src: string; alt: string } | null>(null);
  const openZoom = (src: string, alt: string) => setZoom({ src, alt });

  // 拡大表示中は Esc キーで閉じられるようにする
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoom(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  // スクロール位置に応じて、今読んでいる章の目次項目をハイライトする(スマホ・PC共通)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const goTo = (id: string) => {
    setMenuOpen(false);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ページ上部へ戻るボタン。長いページなので、ある程度スクロールしたら表示する
  const [ showBackToTop, setShowBackToTop ] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <ZoomContext.Provider value={openZoom}>
      <NoIndex title="YICビューティモード専門学校 ガイダンスの使い方 | memorii works" />
      <div className={Styles.page}>
        {/* スマホ用の上部バー(ロゴ代わりの見出し + 目次を開くボタン) */}
        <div className={Styles.mobileBar}>
          <span className={Styles.mobileBarTitle}>ガイダンスの使い方</span>
          <button
            type="button"
            className={Styles.menuButton}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="目次を開く">
            {menuOpen ? <IoCloseOutline /> : <IoMenuOutline />}
          </button>
        </div>

        <div className={Styles.layout}>
          {/* 目次(PCは左に固定表示。スマホはボタンで開くシート) */}
          <nav className={`${Styles.toc} ${menuOpen ? Styles.toc_open : ''}`}>
            <p className={Styles.tocHeading}>目次</p>
            <ul className={Styles.tocList}>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={activeId === s.id ? `${Styles.tocItem} ${Styles.tocItem_active}` : Styles.tocItem}
                    onClick={() => goTo(s.id)}>
                    <span className={Styles.tocIcon}>{s.icon}</span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {menuOpen && <div className={Styles.tocOverlay} onClick={() => setMenuOpen(false)} />}

          {/* 本文 */}
          <main className={Styles.content}>
            <header className={Styles.pageHeader}>
              <h1 className={Styles.pageTitle}>YICビューティモード専門学校 ガイダンスの使い方</h1>
              <p className={Styles.pageLead}>
                校内ガイダンスで話を聞きたいサロンを、当日までに回答していただくための専用ページ
                （
                <a
                  href="https://memorii-works.com/guidance-yic"
                  target="_blank"
                  rel="noreferrer"
                  className={Styles.inlineCode}>
                  memorii-works.com/guidance-yic
                </a>
                ）の使い方です。ログインやアプリのインストールは不要で、スマートフォンのブラウザから
                そのまま回答できます。
              </p>
              <p className={Styles.pageUpdated}>最終更新日：2026年10月21日</p>
            </header>

            {/* やりたいことから探す(機能名を覚えていなくても、目的から該当の章へ飛べる) */}
            <nav className={Styles.goalBox} aria-label="やりたいことから探す">
              <p className={Styles.goalBoxTitle}>
                <IoCompassOutline className={Styles.goalBoxIcon} />
                やりたいことから探す
              </p>
              <div className={Styles.goalList}>
                {GOALS.map((g) => (
                  <button key={g.id} type="button" className={Styles.goalItem} onClick={() => goTo(g.id)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </nav>

            <section
              id="start"
              ref={(el) => (sectionRefs.current['start'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoInformationCircleOutline className={Styles.sectionIcon} />
                はじめに
              </h2>
              <SectionLead>
                このページは、YICビューティモード専門学校の校内ガイダンスで、話を聞きたいサロンを
                事前に回答するための専用ページです。
              </SectionLead>
              <p>
                3つの画面を順番に進みます。気になるサロンを6個以上選んだうえで、話を聞きたい順に
                第1志望〜第6志望まで順位をつけて回答します。
              </p>

              <StepList>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('info')}>
                    学生情報
                  </button>
                  （コース・学年・クラス・名前）を入力します。
                </Step>
                <Step>
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('search')}>
                    気になるサロン
                  </button>
                  の♡を6個以上押します。
                </Step>
                <Step>
                  いいねしたサロンの中から
                  <button type="button" className={Styles.inlineLink} onClick={() => goTo('order')}>
                    志望の順位
                  </button>
                  をつけ、「確定」します。
                </Step>
              </StepList>

              <Shot
                src="/guidance-yic-manual/guidance-intro.jpg"
                alt="ガイダンスページを開いたときの最初の画面"
                caption="ページを開くと最初に表示される画面。目的の説明に続けて、学生情報の入力欄が並ぶ" />

              <TipBox>
                <li>入力した内容は、最後の「確定」（または「内定承諾済み」）を押すまで保存されません。途中でページを閉じると、最初からやり直しになります。</li>
                <li>上の「やりたいことから探す」から、目的に近い項目を選ぶと該当の章まで直接移動できます。</li>
              </TipBox>
            </section>

            <section
              id="info"
              ref={(el) => (sectionRefs.current['info'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoCreateOutline className={Styles.sectionIcon} />
                学生情報を入力する
              </h2>
              <SectionLead>
                コース・学年・クラスは一覧から選択し、名前を入力したら「利用開始」を押します。
              </SectionLead>

              <SubHeading>学生情報入力</SubHeading>
              <StepList>
                <Step>「コース」「学年」「クラス」を、それぞれ一覧から選びます（上の項目を選び直すと、下の項目は選び直しになります）。</Step>
                <Step>「名前」に自分の氏名を入力し、「利用開始」を押します。</Step>
              </StepList>
              <Shot
                src="/guidance-yic-manual/guidance-form-filled.jpg"
                alt="学生情報入力欄に入力した状態の画面"
                hotspots={[
                  { n: 1, x: 50, y: 35 },
                  { n: 2, x: 50, y: 84 },
                ]} />

              <Caution>
                コース・学年・クラスの候補に自分の情報が見当たらない場合は、学校の先生に確認してください。
              </Caution>

              <SubHeading>参加状況を選ぶ</SubHeading>
              <p>
                「利用開始」を押すと、「参加状況の確認」画面に切り替わります。ガイダンス当日に参加する場合は
                「ガイダンスに参加する」を選びます。
              </p>
              <Shot
                src="/guidance-yic-manual/guidance-confirm.jpg"
                alt="参加状況の確認画面"
                hotspots={[{ n: 1, x: 50, y: 52 }]} />

              <div className={Styles.notice}>
                <strong>重要：</strong>
                すでに内定を承諾している場合は「内定承諾済み」を選びます。押すとその場で学校に内定報告が
                送信され、確認画面や取り消しはありません。押し間違えた場合は、学校の就職支援担当の先生、
                または運営（memorii）までご連絡ください。
              </div>
            </section>

            <section
              id="search"
              ref={(el) => (sectionRefs.current['search'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHeartOutline className={Styles.sectionIcon} />
                気になるサロンを選ぶ
              </h2>
              <SectionLead>
                サロンの求人を見ながら、気になるサロンに♡（いいね）を6個以上つけます。
              </SectionLead>

              <SubHeading>サロンに「いいね」する</SubHeading>
              <StepList>
                <Step>サロンの写真の右上にある♡を押します。</Step>
                <Step>6個以上いいねすると、上部の「次に進む」が押せるようになります。</Step>
              </StepList>
              <div className={Styles.shotRow}>
                <Shot
                  src="/guidance-yic-manual/guidance-search-card.jpg"
                  alt="サロンの求人が並ぶ一覧画面(カード表示)"
                  hotspots={[
                    { n: 1, x: 86, y: 30 },
                    { n: 2, x: 85, y: 15 },
                  ]} />
                <Shot
                  src="/guidance-yic-manual/guidance-like-hint.jpg"
                  alt="いいねの件数を知らせるポップアップ"
                  caption="♡を押すたびに、現在の件数を知らせるポップアップが数秒表示される" />
                <Shot
                  src="/guidance-yic-manual/guidance-like-complete.jpg"
                  alt="6個いいねしたときのポップアップ"
                  caption="6個そろうと表示される。「閉じる」を押すと一覧に戻る" />
              </div>

              <SubHeading>表示を切り替える・絞り込む</SubHeading>
              <p>
                右上のアイコンで、写真中心の「カード表示」と、会社名・業種タグが見やすい「リスト表示」を
                切り替えられます。「フィルター」からは、都道府県・フリーワード・給与などで求人を絞り込めます。
              </p>
              <div className={Styles.shotRow}>
                <Shot
                  src="/guidance-yic-manual/guidance-search-list.jpg"
                  alt="サロンの求人が並ぶ一覧画面(リスト表示)"
                  caption="リスト表示。会社名・業種タグ・都道府県が一覧で見やすい" />
                <Shot
                  src="/guidance-yic-manual/guidance-search-filter.jpg"
                  alt="絞り込み(フィルター)画面"
                  caption="都道府県・フリーワード検索・給与などで絞り込める。「保存」を押すと一覧に反映される" />
              </div>

              <TipBox>
                <li>迷ったら、写真の雰囲気や「なんとなく気になる」で選んでも大丈夫です。</li>
                <li>いいねの数に上限はありません。6個を超えて気になるサロンはどんどん押しても問題ありません。</li>
              </TipBox>
            </section>

            <section
              id="order"
              ref={(el) => (sectionRefs.current['order'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoSwapVerticalOutline className={Styles.sectionIcon} />
                志望の順位をつける
              </h2>
              <SectionLead>
                いいねしたサロンの中から、話を聞きたい順に第1志望〜第6志望まで順位をつけ、「確定」します。
              </SectionLead>
              <Shot
                src="/guidance-yic-manual/guidance-order-empty.jpg"
                alt="志望の順位をつける画面(全サロンが未選択の状態)"
                caption="「次に進む」を押すと表示される画面。いいねしたサロンが並び、最初は全員「未選択」" />

              <SubHeading>ドラッグで順位をつける</SubHeading>
              <StepList>
                <Step>サロンの右端にある「≡」を上下にドラッグします。</Step>
                <Step>区切り線より上に動かすと、上から順に第1志望・第2志望…に決まります。</Step>
              </StepList>
              <Shot
                src="/guidance-yic-manual/guidance-order-ranked.jpg"
                alt="2件のサロンに志望順位がついた画面"
                hotspots={[
                  { n: 1, x: 91, y: 23 },
                  { n: 2, x: 50, y: 38 },
                ]} />

              <SubHeading>プルダウンで選ぶ</SubHeading>
              <p>
                「未選択」と表示されているプルダウンをタップしても、「第1志望」〜「第6志望」を直接選べます。
                すでに他のサロンが使っている志望を選ぶと、そちらは入れ替わって「未選択」に戻ります。
              </p>

              <Caution>
                第1志望〜第6志望をすべて選ばないと「確定」できません。足りない志望があるまま「確定」を
                押すと、どれが未選択かが一覧で表示されます。
              </Caution>
              <Shot
                src="/guidance-yic-manual/guidance-order-error.jpg"
                alt="志望が足りないまま確定を押したときのエラー表示"
                caption="「確定」を押したときに志望が足りないと表示される" />

              <SubHeading>回答を送信する</SubHeading>
              <p>
                第1志望〜第6志望をすべて選んだ状態で「確定」を押すと、そのまま学校に送信されます。
              </p>
              <Shot
                src="/guidance-yic-manual/guidance-order-complete.jpg"
                alt="受付完了画面"
                caption="「確定」を押すと表示される受付完了の画面。これでガイダンスの回答は終了" />

              <TipBox>
                <li>並べ替えは何度でもやり直せます。「確定」を押すまでは反映されません。</li>
                <li>「確定」は押すと確認画面なしでただちに学校へ送信されます。内容をよく確認してから押しましょう。</li>
              </TipBox>
            </section>

            <section
              id="faq"
              ref={(el) => (sectionRefs.current['faq'] = el)}
              className={Styles.section}>
              <h2 className={Styles.sectionTitle}>
                <IoHelpCircleOutline className={Styles.sectionIcon} />
                よくある質問
              </h2>
              <SectionLead>
                自分で解決できそうにない場合は、学校の就職支援担当の先生、または運営（memorii）に
                お問い合わせください。
              </SectionLead>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 間違えて「内定承諾済み」を押してしまいました。</p>
                <p className={Styles.faqA}>
                  A. 「内定承諾済み」を押すと、その場で学校に内定報告が送信されます。押し間違えた場合は、
                  学校の就職支援担当の先生、または運営（memorii）にご連絡ください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. いいねの数に上限はありますか。</p>
                <p className={Styles.faqA}>
                  A. 上限はありません。6個以上であれば、何個いいねしても構いません。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 入力の途中でページを閉じてしまいました。</p>
                <p className={Styles.faqA}>
                  A. 最後に「確定」（または「内定承諾済み」）を押すまでは保存されません。もう一度ページを
                  開いて、学生情報の入力からやり直してください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. コース・学年・クラスの候補に自分の情報がありません。</p>
                <p className={Styles.faqA}>
                  A. コース・学年・クラスは、学校に登録されている候補から選ぶ項目です。候補に見当たらない
                  場合は、学校の就職支援担当の先生に確認してください。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 志望の順位は、ドラッグ以外でもつけられますか。</p>
                <p className={Styles.faqA}>
                  A. つけられます。各サロンの横にある「未選択」のプルダウンから、「第1志望」〜「第6志望」を
                  直接選ぶこともできます。
                </p>
              </div>
              <div className={Styles.faqItem}>
                <p className={Styles.faqQ}>Q. 第1志望〜第6志望を全部選ばないと「確定」できませんか。</p>
                <p className={Styles.faqA}>
                  A. はい。YICビューティモード専門学校は順位付け方式のため、第1志望から第6志望まで
                  すべて選ぶ必要があります。足りない志望があると、「確定」を押したときにどれが未選択かが
                  一覧で表示されます。
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* ページ上部へ戻る(ある程度スクロールしたときだけ表示) */}
      {showBackToTop && (
        <button
          type="button"
          className={Styles.backToTop}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="ページ上部へ戻る">
          <IoArrowUpOutline />
        </button>
      )}

      {/* 画像の拡大表示(クリックで開く。背景クリック・✕・Escで閉じる) */}
      {zoom && (
        <div className={Styles.lightbox} role="dialog" aria-modal="true" aria-label={zoom.alt}>
          <div className={Styles.lightboxBg} onClick={() => setZoom(null)} />
          <button type="button" className={Styles.lightboxClose} onClick={() => setZoom(null)} aria-label="閉じる">
            <IoCloseOutline />
          </button>
          <img className={Styles.lightboxImg} src={zoom.src} alt={zoom.alt} />
        </div>
      )}
    </ZoomContext.Provider>
  );
};

export default GuidanceYicManual;
